import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { User } from '../types';
import { isNativeApp, REMOTE_SITE_ORIGIN } from '../apiEnv';
import { Users, Video, LogOut, MessageSquare, Swords } from 'lucide-react';
import { VideoBattle } from './VideoBattle';
import {
  createOnlineRoom,
  createOnlineSessionId,
  OnlineChatMsg,
  OnlineRoomSummary,
  sendOnlineLobbyChat,
  syncOnlineLobby,
} from './onlineHttpApi';

const normalizeSocketPath = (value?: string) => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return '/socket.io';
  const withLeadingSlash = trimmedValue.startsWith('/') ? trimmedValue : `/${trimmedValue}`;
  return withLeadingSlash.replace(/\/+$/, '') || '/socket.io';
};

const getRealtimeConfig = () => {
  const configuredUrl = import.meta.env.VITE_SOCKET_SERVER_URL?.trim();
  const path = normalizeSocketPath(import.meta.env.VITE_SOCKET_SERVER_PATH);
  const socketPort = import.meta.env.VITE_SOCKET_PORT?.trim() || '45679';

  if (configuredUrl) {
    return {
      serverUrl: configuredUrl.replace(/\/+$/, ''),
      path,
      mode: 'explicit-url' as const,
    };
  }

  if (typeof window === 'undefined') {
    return {
      serverUrl: `http://localhost:${socketPort}`,
      path,
      mode: 'server-side' as const,
    };
  }

  const { protocol, hostname, origin } = window.location;
  const isWebProtocol = protocol === 'http:' || protocol === 'https:';

  // 原生 App 的页面来自 Capacitor 内置 localhost（协议为 capacitor:），没有同源后端，
  // 必须指向线上站点，否则会拼出 capacitor://localhost:45679 这种无效地址。
  if (isNativeApp() || !isWebProtocol) {
    return {
      serverUrl: REMOTE_SITE_ORIGIN,
      path,
      mode: 'remote-origin' as const,
    };
  }

  if (protocol === 'https:') {
    return {
      serverUrl: origin,
      path,
      mode: 'same-origin-https' as const,
    };
  }

  return {
    serverUrl: `${protocol}//${hostname}:${socketPort}`,
    path,
    mode: 'direct-port' as const,
  };
};

interface GameLobbyProps {
  user: User;
  onLogout: () => void;
  onBackToMain: () => void;
  onRoomActiveChange?: (active: boolean) => void;
}

export const GameLobby: React.FC<GameLobbyProps> = ({ user, onBackToMain, onRoomActiveChange }) => {
  const reportDebugEvent = (hypothesisId: 'A' | 'B' | 'C' | 'D', location: string, msg: string, data: Record<string, unknown> = {}) => {
    void hypothesisId; void location; void msg; void data;
  };

  const configuredSocketServerUrl = import.meta.env.VITE_SOCKET_SERVER_URL?.trim();
  // 原生 App 的 hostname 也是 localhost，但它的 HTTP 兜底走线上 api.php，必须允许回退。
  const canUseHttpFallback = isNativeApp()
    || (typeof window !== 'undefined'
      && window.location.hostname !== 'localhost'
      && window.location.hostname !== '127.0.0.1');
  const shouldPreferHttpFallback = canUseHttpFallback && !configuredSocketServerUrl;

  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);
  const [transportMode, setTransportMode] = useState<'socket' | 'http'>(shouldPreferHttpFallback ? 'http' : 'socket');
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [joinPasswordInput, setJoinPasswordInput] = useState('');
  const [createRoomNameInput, setCreateRoomNameInput] = useState('');
  const [createPasswordInput, setCreatePasswordInput] = useState('');
  const [activeRoom, setActiveRoom] = useState<string | null>(null);
  const [showJoinRoomsModal, setShowJoinRoomsModal] = useState(false);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [createRoomPending, setCreateRoomPending] = useState(false);
  const [roomSearchInput, setRoomSearchInput] = useState('');
  const [passwordTargetRoomId, setPasswordTargetRoomId] = useState<string | null>(null);
  const [roomPasswordDraft, setRoomPasswordDraft] = useState('');
  
  const [rooms, setRooms] = useState<OnlineRoomSummary[]>([]);
  const [chat, setChat] = useState<OnlineChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef(createOnlineSessionId());
  const handleBackFromRoom = useCallback(() => {
    setActiveRoom(null);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const pendingAutoJoinRef = useRef<{ roomId: string; password?: string } | null>(null);
  const activeRoomRef = useRef<string | null>(null);

  // 从 URL 参数读取邀请信息（如 /online?roomId=ABC&password=123），供深链打开后自动进入房间
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roomId = params.get('roomId');
    if (!roomId) return;
    const password = params.get('password') || undefined;

    const currentRoom = activeRoomRef.current;
    // 已经在被邀请的房间里：直接消费掉参数，避免退出房间后被同一条邀请再次拉回。
    if (currentRoom === roomId) {
      navigate('/online', { replace: true });
      return;
    }
    // 正在别的房间里：询问是否切换，拒绝则丢弃这条邀请（不再残留成"幽灵邀请"）。
    if (currentRoom) {
      const confirmed = window.confirm(
        `收到加入房间 ${roomId} 的邀请，是否切换房间？\nReceived an invite for room ${roomId}. Switch rooms?`
      );
      if (!confirmed) {
        navigate('/online', { replace: true });
        return;
      }
      pendingAutoJoinRef.current = { roomId, password };
      setActiveRoom(null);
      navigate('/online', { replace: true });
      return;
    }

    pendingAutoJoinRef.current = { roomId, password };
    navigate('/online', { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  useEffect(() => {
    if (!pendingAutoJoinRef.current) return;
    if (activeRoom) return;
    if (!socketConnected) return;
    const { roomId, password } = pendingAutoJoinRef.current;
    pendingAutoJoinRef.current = null;
    setJoinPasswordInput(password || '');
    setActiveRoom(roomId);
    setPasswordTargetRoomId(null);
    setRoomPasswordDraft('');
  }, [socketConnected, activeRoom]);

  useEffect(() => {
    onRoomActiveChange?.(Boolean(activeRoom));
  }, [activeRoom, onRoomActiveChange]);

  useEffect(() => {
    setTransportMode(shouldPreferHttpFallback ? 'http' : 'socket');
  }, [shouldPreferHttpFallback]);

  useEffect(() => {
    if (transportMode !== 'socket') {
      setSocket(null);
      return;
    }

    const realtimeConfig = getRealtimeConfig();
    const realtimeTarget = `${realtimeConfig.serverUrl}${realtimeConfig.path}`;
    const newSocket = io(realtimeConfig.serverUrl, {
      path: realtimeConfig.path,
      transports: ['websocket', 'polling'],
      reconnection: true
    });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      setSocketConnected(true);
      setSocketError(null);
    });
    newSocket.on('online-users', (count: number) => setOnlineUsers(count));
    newSocket.on('active-rooms', (activeRooms) => {
      reportDebugEvent('C', 'GameLobby.tsx:active-rooms', 'received active rooms update', {
        count: Array.isArray(activeRooms) ? activeRooms.length : null,
        activeRooms
      });
      setRooms(activeRooms);
    });
    newSocket.on('lobby-chat', (msgs) => setChat([...msgs]));
    newSocket.on('connect_error', (error) => {
      console.error('Realtime connection failed:', error);
      if (canUseHttpFallback) {
        setTransportMode('http');
        setSocket(null);
        setSocketConnected(false);
        newSocket.removeAllListeners();
        newSocket.disconnect();
        return;
      }
      setSocketConnected(false);
      setSocketError(`Realtime server unavailable at ${realtimeTarget}. Set VITE_SOCKET_SERVER_URL to your Socket.io server.`);
    });
    newSocket.on('disconnect', () => {
      setSocketConnected(false);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [canUseHttpFallback, transportMode]);

  useEffect(() => {
    if (transportMode !== 'http' || activeRoom) return;

    let cancelled = false;

    const syncLobby = async () => {
      try {
        const payload = await syncOnlineLobby(sessionIdRef.current, user);
        if (cancelled) return;
        setOnlineUsers(payload.onlineUsers || 0);
        setRooms(payload.activeRooms || []);
        setChat(payload.lobbyChat || []);
        setSocketConnected(true);
        setSocketError(null);
      } catch (error) {
        if (cancelled) return;
        setSocketConnected(false);
        setSocketError((error as Error).message || 'HTTP lobby sync failed.');
      }
    };

    void syncLobby();
    const intervalId = window.setInterval(() => {
      void syncLobby();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeRoom, transportMode, user]);

  useEffect(() => {
    if (chatContainerRef.current) {
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [chat]);

  const handleDirectJoin = (roomId: string, hasPassword?: boolean) => {
    if (!socketConnected) {
      if (transportMode === 'http') {
        setSocketError('HTTP fallback is still syncing room state. Please try again in a moment.');
        return;
      }
      const realtimeConfig = getRealtimeConfig();
      const realtimeTarget = `${realtimeConfig.serverUrl}${realtimeConfig.path}`;
      setSocketError(`Realtime server unavailable at ${realtimeTarget}. Set VITE_SOCKET_SERVER_URL to your Socket.io server.`);
      return;
    }
    if (hasPassword) {
      if (passwordTargetRoomId === roomId && roomPasswordDraft.trim()) {
        setJoinPasswordInput(roomPasswordDraft.trim());
        setActiveRoom(roomId);
        setRoomPasswordDraft('');
        setPasswordTargetRoomId(null);
        setSocketError(null);
        return;
      }
      setPasswordTargetRoomId(roomId);
      setRoomPasswordDraft('');
      return;
    }
    setActiveRoom(roomId);
    setJoinPasswordInput('');
    setPasswordTargetRoomId(null);
    setRoomPasswordDraft('');
  };

  const [localGuestName] = useState(() => `Guest-${Math.floor(Math.random() * 1000)}`);
  
  const handleCreateRoom = () => {
    const newRoomId = Math.random().toString(36).substring(2, 8).toUpperCase();
    // #region debug-point A:create-room-click
    reportDebugEvent('A', 'GameLobby.tsx:handleCreateRoom', 'create room clicked', {
      transportMode,
      socketConnected,
      hasSocket: Boolean(socket),
      roomName: createRoomNameInput.trim(),
      hasPassword: Boolean(createPasswordInput.trim()),
      sessionId: sessionIdRef.current,
      userEmail: user.email
    });
    // #endregion
    if (!socketConnected) {
      if (transportMode === 'http') {
        // #region debug-point A:create-room-blocked-http
        reportDebugEvent('A', 'GameLobby.tsx:handleCreateRoom', 'create room blocked because http sync not ready', {
          transportMode,
          socketConnected
        });
        // #endregion
        setSocketError('HTTP fallback is still syncing room state. Please try again in a moment.');
        return;
      }
      const realtimeConfig = getRealtimeConfig();
      const realtimeTarget = `${realtimeConfig.serverUrl}${realtimeConfig.path}`;
      // #region debug-point A:create-room-blocked-socket
      reportDebugEvent('A', 'GameLobby.tsx:handleCreateRoom', 'create room blocked because realtime socket unavailable', {
        transportMode,
        socketConnected,
        realtimeTarget
      });
      // #endregion
      setSocketError(`Realtime server unavailable at ${realtimeTarget}. Set VITE_SOCKET_SERVER_URL to your Socket.io server.`);
      return;
    }
    if (transportMode === 'http') {
      const displayName = user.uid === 'guest' ? localGuestName : user.displayName;
      const roomName = createRoomNameInput.trim() || `${displayName}'s Room`;
      setCreateRoomPending(true);
      setSocketError(null);
      void createOnlineRoom(
        sessionIdRef.current,
        user,
        newRoomId,
        roomName,
        createPasswordInput.trim() || undefined
      )
        .then(() => {
          // #region debug-point C:create-room-success
          reportDebugEvent('C', 'GameLobby.tsx:handleCreateRoom', 'create room request resolved', {
            transportMode,
            roomId: newRoomId,
            roomName
          });
          // #endregion
          setActiveRoom(newRoomId);
          setJoinPasswordInput(createPasswordInput.trim() || '');
          setCreateRoomNameInput('');
          setCreatePasswordInput('');
          setCreateRoomPending(false);
          setSocketError(null);
        })
        .catch((error) => {
          // #region debug-point B:create-room-failed
          reportDebugEvent('B', 'GameLobby.tsx:handleCreateRoom', 'create room request rejected', {
            transportMode,
            roomId: newRoomId,
            roomName,
            errorName: error instanceof Error ? error.name : 'unknown',
            errorMessage: error instanceof Error ? error.message : String(error)
          });
          // #endregion
          setCreateRoomPending(false);
          setSocketError((error as Error).message || 'Create room failed.');
        });
      return;
    }
    if (socket) {
      const displayName = user.uid === 'guest' ? localGuestName : user.displayName;
      const roomName = createRoomNameInput.trim() || `${displayName}'s Room`;
      socket.emit('create-room', { 
        roomId: newRoomId, 
        roomName,
        password: createPasswordInput.trim() || undefined,
        user: { name: displayName, email: user.email } 
      });
      // #region debug-point C:create-room-socket-emitted
      reportDebugEvent('C', 'GameLobby.tsx:handleCreateRoom', 'socket create-room emitted', {
        roomId: newRoomId,
        roomName,
        userEmail: user.email
      });
      // #endregion
      setActiveRoom(newRoomId);
      setJoinPasswordInput(createPasswordInput.trim() || ''); // Auto fill password for creator
      setCreateRoomNameInput('');
      setCreatePasswordInput('');
      setCreateRoomPending(false);
    }
  };

  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    if (transportMode === 'http') {
      void sendOnlineLobbyChat(sessionIdRef.current, user, chatInput)
        .then((payload) => {
          setChat(payload.lobbyChat || []);
          setOnlineUsers(payload.onlineUsers || onlineUsers);
          setRooms(payload.activeRooms || rooms);
          setChatInput('');
          setSocketError(null);
        })
        .catch((error) => {
          setSocketError((error as Error).message || 'Send chat failed.');
        });
      return;
    }
    if (!socket) return;
    const displayName = user.uid === 'guest' ? localGuestName : user.displayName;
    socket.emit('send-lobby-chat', { message: chatInput, sender: displayName });
    setChatInput('');
  };

  if (activeRoom && (transportMode === 'http' || socket)) {
    const virtualUser = user.uid === 'guest' ? { ...user, displayName: localGuestName } : user;
    return (
      <VideoBattle
        user={virtualUser}
        roomId={activeRoom}
        roomPassword={joinPasswordInput}
        socket={socket}
        transportMode={transportMode}
        sessionId={sessionIdRef.current}
        onBack={handleBackFromRoom}
      />
    );
  }

  const filteredRooms = rooms.filter((room) => {
    const keyword = roomSearchInput.trim().toLowerCase();
    if (!keyword) return true;
    return room.name.toLowerCase().includes(keyword) || room.id.toLowerCase().includes(keyword);
  });

  // 联机通道尚未就绪（Socket 握手或 HTTP 同步中）时展示不可关闭的加载提示，连接成功后自动消失
  const isLobbyConnecting = !socketConnected && !socketError;

  return (
    <div className="h-full w-full flex flex-col items-center justify-start relative overflow-y-auto p-4 md:p-8 text-white font-rajdhani">
      <div className="w-full max-w-6xl flex justify-between items-center mb-6 md:mb-12 relative z-10">
        <button 
          onClick={onBackToMain} 
          className="flex items-center gap-1 md:gap-2 text-zinc-400 hover:text-white transition-colors uppercase tracking-widest font-orbitron text-xs md:text-base px-2 py-1 md:px-4 md:py-2 bg-black/50 border border-zinc-800 rounded-xl hover:border-zinc-600"
        >
          <LogOut className="rotate-180" size={16} /> <span className="hidden sm:inline">MAIN MENU</span><span className="sm:hidden">BACK</span>
        </button>

        <h1 className="text-xl md:text-5xl font-orbitron font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 uppercase tracking-[0.1em] md:tracking-[0.2em] drop-shadow-[0_0_10px_rgba(59,130,246,0.5)]">
          ONLINE LOBBY
        </h1>

        <div className="flex items-center gap-2 md:gap-4 bg-black/50 border border-zinc-800 rounded-xl px-2 py-1 md:px-4 md:py-2">
          <div className="flex items-center gap-1 md:gap-2">
            <Users size={14} className="text-emerald-400" />
            <span className="font-orbitron text-emerald-400 text-[10px] md:text-sm tracking-widest">{onlineUsers} <span className="hidden sm:inline">ONLINE</span></span>
          </div>
          <div className="w-px h-3 md:h-4 bg-zinc-700 mx-1 md:mx-2"></div>
          <div className="flex items-center gap-1 md:gap-2">
            <div className="w-4 h-4 md:w-6 md:h-6 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-[10px] md:text-xs font-bold shadow-[0_0_10px_rgba(59,130,246,0.5)]">
              {user.uid === 'guest' ? 'G' : user.displayName.charAt(0).toUpperCase()}
            </div>
            <span className="font-orbitron text-zinc-300 text-[10px] md:text-sm tracking-widest truncate max-w-[60px] md:max-w-[100px]">{user.uid === 'guest' ? localGuestName : user.displayName}</span>
          </div>
        </div>
      </div>

      <div className="w-full max-w-6xl flex flex-col lg:flex-row gap-4 md:gap-6 flex-1 min-h-0">
        {/* Left Side: Join/Create Room */}
        <div className="flex flex-col gap-4 md:gap-6 relative z-40 pointer-events-auto lg:flex-1">
          {socketError && (
            <div className="w-full bg-red-950/60 border border-red-500/50 rounded-2xl p-4 text-red-200">
              <div className="font-orbitron text-xs md:text-sm tracking-widest uppercase mb-1">Realtime Connection Error</div>
              <div className="text-sm font-rajdhani break-words">{socketError}</div>
            </div>
          )}
          <div className="w-full rounded-2xl p-4 md:p-8 relative overflow-hidden group">
            <div className="absolute -inset-[1px] bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            
            <div className="flex flex-col items-center gap-4 md:gap-6 relative z-10">
              <div className="p-3 md:p-4 rounded-full border border-blue-500/30 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                <Swords size={40} className="text-blue-400 md:w-12 md:h-12" />
              </div>
              
              <div className="text-center space-y-1 md:space-y-2">
                <h2 className="text-xl md:text-2xl font-orbitron font-bold tracking-[0.2em] text-white">ONLINE BATTLE</h2>
                <p className="text-zinc-400 font-rajdhani text-[10px] md:text-sm tracking-widest uppercase">Create a room instantly, or browse existing rooms to join / 快速创建房间，或浏览并加入已有房间</p>
              </div>

              <div className="w-full max-w-md flex flex-col gap-3 md:gap-4 mt-2 md:mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateRoomModal(true)}
                    disabled={!socketConnected || createRoomPending}
                    className={`w-full rounded-md border px-4 py-4 font-orbitron font-bold tracking-[0.15em] uppercase transition-all duration-300 text-xs md:text-sm ${
                      socketConnected && !createRoomPending
                        ? 'border-purple-500 bg-purple-600/20 text-purple-300 hover:bg-purple-600/35 shadow-[0_0_20px_rgba(168,85,247,0.25)]'
                        : 'border-zinc-700 bg-black/50 text-zinc-500 cursor-not-allowed'
                    }`}
                  >
                    {createRoomPending ? '创建中...' : '创建房间'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJoinRoomsModal(true)}
                    className="w-full rounded-md border border-blue-500 bg-blue-600/20 text-blue-300 hover:bg-blue-600/35 shadow-[0_0_20px_rgba(59,130,246,0.25)] px-4 py-4 font-orbitron font-bold tracking-[0.15em] uppercase transition-all duration-300 text-xs md:text-sm"
                  >
                    加入房间
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Right Side: Global Chat */}
        <div className="w-full lg:w-96 flex flex-col gap-4 relative z-40 pointer-events-auto flex-1 min-h-0 lg:flex-none">
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-4 md:p-6 flex flex-col flex-1 min-h-[400px] pointer-events-auto relative z-40">
            <h3 className="font-orbitron text-zinc-400 text-xs md:text-sm tracking-widest uppercase mb-4 flex items-center gap-2 border-b border-zinc-800 pb-2 shrink-0">
               <MessageSquare size={16} /> Global Lobby Chat / 大厅频道
             </h3>
             <div ref={chatContainerRef} className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2 custom-scrollbar pointer-events-auto relative z-50">
               {chat.length === 0 ? (
                 <div className="text-center text-zinc-600 py-4 font-rajdhani text-sm">Be the first to say hello! / 来打个招呼吧！</div>
               ) : (
                 chat.map((msg, i) => (
                   <div key={i} className="text-sm bg-black/40 rounded-lg p-2 border border-zinc-800/50">
                     <div className="flex justify-between items-end mb-1">
                        <span className={`font-bold ${msg.sender === (user.uid === 'guest' ? localGuestName : user.displayName) ? 'text-purple-400' : 'text-blue-400'}`}>{msg.sender}</span>
                        <span className="text-[10px] text-zinc-600 font-mono">{new Date(msg.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                     </div>
                     <span className="text-zinc-300 font-rajdhani break-words whitespace-pre-wrap">{msg.text}</span>
                   </div>
                 ))
               )}
             </div>
             <form onSubmit={handleSendChat} className="flex gap-2 relative z-50 pointer-events-auto mt-2 md:mt-0">
               <input 
                 type="text" 
                 value={chatInput} 
                 onChange={e => setChatInput(e.target.value)} 
                 placeholder="Type a message... / 发送消息..."
                 className="flex-1 bg-black border border-zinc-700 rounded-xl px-3 md:px-4 py-2 text-xs md:text-sm font-rajdhani text-white focus:outline-none focus:border-blue-500 transition-colors pointer-events-auto"
               />
               <button 
                type="submit" 
                disabled={!chatInput.trim()}
                className={`px-3 md:px-4 py-2 rounded-xl text-[10px] md:text-xs font-orbitron transition-colors pointer-events-auto ${chatInput.trim() ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-[0_0_10px_rgba(37,99,235,0.3)]' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
               >
                 Send
               </button>
             </form>
          </div>
        </div>
      </div>
      {showJoinRoomsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-zinc-900 border border-zinc-700 rounded-2xl p-4 md:p-6 overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 border-b border-zinc-800 pb-3 shrink-0">
              <h3 className="font-orbitron text-zinc-200 text-sm md:text-base tracking-widest uppercase flex items-center gap-2">
                <Video size={16} /> 加入房间 / Active Rooms ({rooms.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowJoinRoomsModal(false)}
                className="text-zinc-500 hover:text-white transition-colors text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mb-4 shrink-0">
              <input
                type="text"
                value={roomSearchInput}
                onChange={(e) => setRoomSearchInput(e.target.value)}
                placeholder="SEARCH BY ROOM NAME OR ID / 搜索房间名或房间号"
                className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-center font-orbitron tracking-[0.08em] text-xs md:text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-zinc-600"
              />
            </div>
            <div className="overflow-y-auto space-y-3 pr-1 custom-scrollbar">
              {filteredRooms.length === 0 ? (
                <div className="text-center text-zinc-600 py-4 font-rajdhani text-sm">No active rooms found. Create one! / 暂无房间，去创建一个吧！</div>
              ) : (
                filteredRooms.map(room => (
                  <div key={room.id} className="bg-black/40 border border-zinc-800 rounded-xl p-4 hover:border-blue-500/50 transition-colors">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-orbitron font-bold text-blue-400 tracking-wider flex items-center gap-2">
                          {room.name}
                          {room.hasPassword && <span className="text-[10px] bg-red-500/20 text-red-400 px-1 rounded border border-red-500/30">LOCKED</span>}
                          {room.isSaved && <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1 rounded border border-emerald-500/30">SAVED</span>}
                        </div>
                        <div className="text-xs text-zinc-500 font-rajdhani">ID: {room.id}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 text-zinc-400 text-xs font-orbitron">
                          <Users size={12} /> {room.userCount}
                        </div>
                        <button
                          onClick={() => handleDirectJoin(room.id, room.hasPassword)}
                          className="px-4 py-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white border border-blue-500/50 rounded text-xs font-orbitron tracking-widest transition-colors"
                        >
                          {room.hasPassword && passwordTargetRoomId === room.id ? '确认加入' : 'JOIN'}
                        </button>
                      </div>
                    </div>
                    {room.hasPassword && passwordTargetRoomId === room.id && (
                      <div className="mt-3 flex flex-col md:flex-row gap-2">
                        <input
                          type="password"
                          value={roomPasswordDraft}
                          onChange={(e) => setRoomPasswordDraft(e.target.value)}
                          placeholder="ENTER ROOM PASSWORD / 输入房间密码"
                          className="flex-1 bg-black/60 border border-zinc-700 rounded-xl py-2 px-4 text-center font-orbitron tracking-[0.08em] text-xs md:text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-zinc-600"
                        />
                        <button
                          type="button"
                          onClick={() => handleDirectJoin(room.id, true)}
                          disabled={!roomPasswordDraft.trim()}
                          className={`px-4 py-2 rounded-xl text-xs font-orbitron tracking-widest transition-colors ${
                            roomPasswordDraft.trim()
                              ? 'bg-blue-600 text-white hover:bg-blue-500 border border-blue-500/50'
                              : 'bg-zinc-800 text-zinc-500 cursor-not-allowed border border-zinc-700'
                          }`}
                        >
                          确认加入
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {showCreateRoomModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        >
          <div
            className="w-full max-w-md flex flex-col bg-zinc-900 border border-zinc-700 rounded-2xl p-4 md:p-6 overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 border-b border-zinc-800 pb-3 shrink-0">
              <h3 className="font-orbitron text-zinc-200 text-sm md:text-base tracking-widest uppercase flex items-center gap-2">
                <Swords size={16} /> 创建房间 / Create Room
              </h3>
              <button
                type="button"
                onClick={() => setShowCreateRoomModal(false)}
                className="text-zinc-500 hover:text-white transition-colors text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-zinc-400 font-orbitron text-xs tracking-widest uppercase">房间名 / Room Name</span>
                <input
                  type="text"
                  value={createRoomNameInput}
                  onChange={(e) => setCreateRoomNameInput(e.target.value)}
                  placeholder="请输入房间名 / Enter room name"
                  className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-base font-rajdhani text-white focus:border-blue-500 focus:outline-none transition-all placeholder:text-zinc-600"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-zinc-400 font-orbitron text-xs tracking-widest uppercase">房间密码（可选）/ Password (Optional)</span>
                <input
                  type="password"
                  value={createPasswordInput}
                  onChange={(e) => setCreatePasswordInput(e.target.value)}
                  placeholder="留空则无需密码 / Leave empty for no password"
                  className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-base font-rajdhani text-white focus:border-blue-500 focus:outline-none transition-all placeholder:text-zinc-600"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowCreateRoomModal(false);
                  handleCreateRoom();
                }}
                disabled={createRoomPending}
                className="w-full mt-2 rounded-xl border border-purple-500 bg-purple-600/20 text-purple-300 hover:bg-purple-600/35 px-4 py-3 font-orbitron font-bold tracking-[0.15em] uppercase transition-all duration-300 text-xs md:text-sm"
              >
                {createRoomPending ? '创建中...' : '确认创建 / Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isLobbyConnecting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-zinc-900/95 border border-zinc-700 rounded-2xl px-8 py-6 shadow-2xl">
            <div className="w-10 h-10 rounded-full border-2 border-zinc-700 border-t-purple-500 animate-spin" />
            <div className="text-center space-y-1">
              <div className="font-orbitron text-sm tracking-widest uppercase text-purple-300">Connecting</div>
              <div className="font-rajdhani text-xs text-zinc-400 tracking-widest">正在连接联机服务器...</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
